import React, { PureComponent } from "react";
import classNames from "classnames";
import { differenceInMilliseconds } from "date-fns";
import { eventListShape } from "./shapes";

export default class Navigation extends PureComponent {
  static displayName = "Timeline.Navigation";
  static propTypes = eventListShape;

  constructor(props) {
    super(props);

    this.navRef = React.createRef();
  }

  get id() {
    return this.props.id;
  }

  get events() {
    return this.props.events;
  }

  get eventCount() {
    return this.events.length;
  }

  get active() {
    return this.props.active;
  }

  get startDate() {
    return this.events[0].dateObj;
  }

  get endDate() {
    return this.events[this.eventCount - 1].dateObj;
  }

  get fullInterval() {
    return differenceInMilliseconds(this.startDate, this.endDate);
  }

  get tabbable() {
    if (!this.navRef || !this.navRef.current) return null;
    return Array.from(this.navRef.current.querySelectorAll("button"));
  }

  handleKeyDown = (key) => {
    if (key === 37) this.tabLeft();
    if (key === 39) this.tabRight();
  };

  tabLeft() {
    const buttons = this.tabbable;
    if (!buttons) return null;
    const focused = document.activeElement;
    const focusableIndex = buttons.indexOf(focused);

    focusableIndex === 0
      ? buttons[buttons.length - 1].focus()
      : buttons[focusableIndex - 1].focus();
  }

  tabRight() {
    const buttons = this.tabbable;
    if (!buttons) return null;
    const focused = document.activeElement;
    const focusableIndex = buttons.indexOf(focused);

    focusableIndex === buttons.length - 1
      ? buttons[0].focus()
      : buttons[focusableIndex + 1].focus();
  }

  getDuration(first, last) {
    return differenceInMilliseconds(first, last);
  }

  itemPosition(date) {
    const durationFromStart = this.getDuration(this.startDate, date);
    const position = durationFromStart / this.fullInterval;
    return `${position * 100}%`;
  }

  renderIcon(size, className) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={className}
        aria-hidden
      >
        <circle cx={size / 2} cy={size / 2} r={size / 2} />
      </svg>
    );
  }

  render() {
    if (this.events.length <= 1) return null;

    return (
      <div className="m-timeline-nav">
        <ul
          ref={this.navRef}
          role="tablist"
          aria-label="Timeline"
          onKeyDown={({ which }) => this.handleKeyDown(which)}
          className="m-timeline-nav__list"
        >
          {this.events.map((event, i) => (
            <li
              key={i}
              role="presentation"
              className="m-timeline-nav__item"
              style={{ left: this.itemPosition(event.dateObj) }}
            >
              <button
                id={`${this.id}-${i}-tab`}
                role="tab"
                tabIndex={i > 0 ? -1 : 0}
                aria-controls={`${this.id}-${i}-panel`}
                aria-selected={this.active === i}
                aria-label={event.dateObj.year}
                onClick={() => {
                  this.props.onUpdate(i);
                }}
                className={classNames({
                  "m-timeline-nav__button": true,
                  "m-timeline-nav__button--active": this.active === i,
                })}
              >
                {this.renderIcon(
                  16,
                  "m-timeline-nav__icon m-timeline-nav__icon--base"
                )}
                {this.renderIcon(
                  36,
                  "m-timeline-nav__icon m-timeline-nav__icon--active"
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}
